import { Card, SubCard, DataTable, Pill } from "@/components/report/atoms";
import { TaggedText, TagPills } from "@/components/report/TaggedText";
import { Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const relevanceTone = (r) => {
  const v = (r || "").toLowerCase();
  if (v === "high" || v === "highly relevant") return "green";
  if (v === "low" || v === "low relevance") return "red";
  return "amber";
};

export const EnhancementSection = ({ enhancement }) => {
  const [copied, setCopied] = useState(false);
  if (!enhancement) return null;
  const r = enhancement.result || {};

  const sectionA = r.section_a_primary_data_summary || [];
  const sectionB = r.section_b_enhanced_insights || [];
  const sectionC = r.section_c_enhanced_opportunities || [];
  const email = r.section_d_cxo_email || {};
  const slides = r.section_e_ppt_storyline || [];
  const levers = r.section_f_relationship_levers || {};
  const impact = r.section_g_impact_assessment || {};
  const gaps = r.section_h_gaps || {};

  const onCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${email.subject || ""}\n\n${email.body || ""}`);
      setCopied(true);
      toast.success("Enhanced email copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const SectionHeader = ({ letter, title, sub }) => (
    <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-[#262626]">
      <span className="kpi-label text-[#525252]">/{letter}</span>
      <h3 className="font-display text-lg md:text-xl tracking-tight text-white">{title}</h3>
      {sub && <span className="kpi-label ml-auto">{sub}</span>}
    </div>
  );

  return (
    <div data-testid="enhancement-section" className="space-y-10">
      <div className="flex items-start justify-between gap-6 mb-2">
        <div>
          <div className="kpi-label text-[#0A84FF] mb-1">/ PRIMARY DATA AMPLIFICATION</div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-white">Enhanced Intelligence</h2>
          <p className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">
            Overlay of your internal knowledge on the base report. Items tagged inline with
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/40">Primary Insight</span>·
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40">High Impact</span>·
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase bg-[#5E5CE6]/20 text-[#a9a7f8] border-[#5E5CE6]/40">Relationship</span>·
            <span className="mx-1 inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono-data tracking-wider uppercase bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40">Proof Point</span>
            are derived from your primary data.
          </p>
        </div>
        {enhancement.created_by_name && (
          <div className="text-right hidden md:block">
            <div className="kpi-label">Enhanced by</div>
            <div className="text-sm text-white mt-1 font-display tracking-tight">{enhancement.created_by_name}</div>
            {enhancement.created_at && (
              <div className="kpi-label mt-0.5">{new Date(enhancement.created_at).toLocaleString()}</div>
            )}
          </div>
        )}
      </div>

      {/* Section A */}
      <div>
        <SectionHeader letter="A" title="Primary Data Summary" sub={`${sectionA.length} input${sectionA.length === 1 ? "" : "s"}`}/>
        {sectionA.length === 0 ? (
          <Card className="p-5">
            <div className="text-sm text-[#A3A3A3]">No primary data provided — enhancement is limited to generic uplift.</div>
          </Card>
        ) : (
          <DataTable
            testId="enh-section-a-table"
            columns={[
              { key: "input_type", label: "Input Type" },
              { key: "description", label: "Description" },
              { key: "relevance", label: "Relevance" },
              { key: "where_used", label: "Where Used" },
            ]}
            rows={sectionA.map((s) => ({
              ...s,
              relevance: <Pill tone={relevanceTone(s.relevance)}>{s.relevance}</Pill>,
            }))}
          />
        )}
      </div>

      {/* Section B */}
      <div>
        <SectionHeader letter="B" title="Enhanced Account Insights"/>
        <Card className="p-5 space-y-4" data-testid="enh-section-b">
          {sectionB.length === 0 ? <span className="text-sm text-[#525252]">—</span> : sectionB.map((b, i) => (
            <div key={`enh-b-${b.text || b.insight || i}-${i}`} className="pb-4 border-b border-[#1F1F1F] last:border-b-0 last:pb-0">
              <TagPills tags={b.tags}/>
              <p className="text-sm text-[#E5E5E5] leading-relaxed mt-2">
                <TaggedText text={b.text || b.insight || ""}/>
              </p>
            </div>
          ))}
        </Card>
      </div>

      {/* Section C */}
      <div>
        <SectionHeader letter="C" title="Enhanced Deal Opportunities" sub={`${sectionC.length} opportunit${sectionC.length === 1 ? "y" : "ies"}`}/>
        <div className="space-y-3" data-testid="enh-section-c">
          {sectionC.length === 0 ? <span className="text-sm text-[#525252]">—</span> : sectionC.map((c, i) => (
            <Card key={`enh-c-${c.area || c.problem || i}-${i}`} className="p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <TagPills tags={c.tags}/>
                {c.deal_value && <div className="font-mono-data text-lg text-white whitespace-nowrap">{c.deal_value}</div>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="kpi-label mb-1">Problem</div>
                  <p className="text-sm text-[#E5E5E5] leading-relaxed"><TaggedText text={c.problem}/></p>
                </div>
                <div>
                  <div className="kpi-label mb-1">Proposed Solution</div>
                  <p className="text-sm text-[#E5E5E5] leading-relaxed"><TaggedText text={c.solution}/></p>
                </div>
                <div>
                  <div className="kpi-label mb-1">Target Persona</div>
                  <p className="text-sm text-white">{c.persona || "—"}</p>
                </div>
                <div>
                  <div className="kpi-label mb-1">Primary Data Used</div>
                  <p className="text-sm text-[#0A84FF] leading-relaxed">{c.primary_data_used || "—"}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Section D */}
      <div>
        <SectionHeader letter="D" title="Hyper-Personalised CXO Email"/>
        <div className="bg-[#141414] border border-[#262626] rounded-sm" data-testid="enh-section-d">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
            <div>
              <div className="kpi-label">/ TO · {email.to_role || "SENIOR CXO"}</div>
              <div className="text-base text-white font-display tracking-tight mt-0.5">{email.subject || "—"}</div>
            </div>
            <button
              type="button"
              onClick={onCopyEmail}
              data-testid="enh-copy-email-btn"
              className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040] gap-2 flex items-center px-3 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-[#32D74B]"/> : <Copy className="w-3.5 h-3.5"/>}
              <span className="text-xs font-mono-data tracking-wider uppercase">{copied ? "Copied" : "Copy"}</span>
            </button>
          </div>
          <div className="p-5 text-sm text-[#E5E5E5] leading-relaxed whitespace-pre-wrap">
            <TaggedText text={email.body || "—"}/>
          </div>
        </div>
      </div>

      {/* Section E */}
      <div>
        <SectionHeader letter="E" title="PPT Storyline · Primary-Data Grounded" sub={`${slides.length} slides`}/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enh-section-e">
          {slides.map((s, i) => (
            <div key={`enh-e-${s.slide || s.title || i}-${i}`} className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <div className="kpi-label text-[#0A84FF] mb-2">SLIDE · {String(s.slide || i + 1).padStart(2, "0")}</div>
              <h4 className="font-display text-lg text-white tracking-tight mb-2 leading-tight">{s.title}</h4>
              <p className="text-sm text-[#A3A3A3] leading-relaxed"><TaggedText text={s.content || ""}/></p>
              {s.primary_data_note && (
                <div className="mt-3 pt-3 border-t border-[#1F1F1F] kpi-label text-[#0A84FF]">
                  Primary data · {s.primary_data_note}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Section F */}
      <div>
        <SectionHeader letter="F" title="Relationship & Trust Levers"/>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="enh-section-f">
          <SubCard title="Existing Relationships">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(levers.existing_relationships || []).map((x, i) => <li key={`er-${x}-${i}`} className="flex gap-2"><span className="text-[#5E5CE6] shrink-0">◆</span>{x}</li>) || <span className="text-[#525252]">—</span>}
            </ul>
          </SubCard>
          <SubCard title="Warm Entry Points">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(levers.warm_entry_points || []).map((x, i) => <li key={`wep-${x}-${i}`} className="flex gap-2"><span className="text-[#0A84FF] shrink-0">→</span>{x}</li>) || <span className="text-[#525252]">—</span>}
            </ul>
          </SubCard>
          <SubCard title="Trust Elements">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(levers.trust_elements || []).map((x, i) => <li key={`te-${x}-${i}`} className="flex gap-2"><span className="text-[#32D74B] shrink-0">✓</span>{x}</li>) || <span className="text-[#525252]">—</span>}
            </ul>
          </SubCard>
        </div>
      </div>

      {/* Section G */}
      <div>
        <SectionHeader letter="G" title="Impact Assessment"/>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-testid="enh-section-g">
          <Card className="p-5">
            <div className="kpi-label mb-1">Relevance Uplift</div>
            <div className="font-mono-data text-2xl text-white mt-1">{impact.relevance_uplift || "—"}</div>
          </Card>
          <Card className="p-5">
            <div className="kpi-label mb-1">Conversion Probability</div>
            <div className="font-mono-data text-2xl text-[#32D74B] mt-1">{impact.conversion_probability_increase || "—"}</div>
          </Card>
          <Card className="p-5">
            <div className="kpi-label mb-1">Deal Size Uplift</div>
            <div className="font-mono-data text-lg text-white mt-1">{impact.deal_size_uplift || "—"}</div>
          </Card>
        </div>
        {impact.reasoning && (
          <Card className="p-5">
            <div className="kpi-label mb-2">Reasoning</div>
            <p className="text-sm text-[#E5E5E5] leading-relaxed">{impact.reasoning}</p>
          </Card>
        )}
      </div>

      {/* Section H */}
      <div>
        <SectionHeader letter="H" title="Gaps in Primary Data" sub="Collect next"/>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enh-section-h">
          <SubCard title="Missing Inputs">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(gaps.missing_inputs || []).map((x, i) => <li key={`mi-${x}-${i}`} className="flex gap-2"><span className="text-[#FF9F0A] shrink-0">!</span>{x}</li>)}
            </ul>
          </SubCard>
          <SubCard title="Critical Contacts Missing">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(gaps.critical_contacts_missing || []).map((x, i) => <li key={`ccm-${x}-${i}`} className="flex gap-2"><span className="text-[#FF9F0A] shrink-0">!</span>{x}</li>)}
            </ul>
          </SubCard>
          <SubCard title="Case Studies Needed">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(gaps.case_studies_needed || []).map((x, i) => <li key={`csn-${x}-${i}`} className="flex gap-2"><span className="text-[#FF9F0A] shrink-0">!</span>{x}</li>)}
            </ul>
          </SubCard>
          <SubCard title="BU-Level Insights Lacking">
            <ul className="space-y-1.5 text-sm text-[#E5E5E5]">
              {(gaps.bu_level_insights_lacking || []).map((x, i) => <li key={`buil-${x}-${i}`} className="flex gap-2"><span className="text-[#FF9F0A] shrink-0">!</span>{x}</li>)}
            </ul>
          </SubCard>
        </div>
        {gaps.recommended_next_collection && gaps.recommended_next_collection.length > 0 && (
          <Card className="p-5 mt-4 border-[#0A84FF]/40">
            <div className="kpi-label text-[#0A84FF] mb-2">/ RECOMMENDED NEXT · COLLECT</div>
            <ol className="space-y-2 text-sm text-[#E5E5E5]">
              {gaps.recommended_next_collection.map((x, i) => (
                <li key={`rnc-${x}-${i}`} className="flex gap-3">
                  <span className="kpi-label text-[#525252] shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span>{x}</span>
                </li>
              ))}
            </ol>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EnhancementSection;
